import React, { useState, useEffect, useRef } from "react";
import { API_BASE_URL } from "../../Common/Common";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import "./Modal.css";
import "./MenuModal.css";
import "./CommandPalette.css";

const AI_SEARCH_URL = `${API_BASE_URL}/command_search_gpt5.6-luna.php`;
const DOCS_QA_URL = `${API_BASE_URL}/docs_qa.php`;
const WHISPER_URL = `${API_BASE_URL}/whisper_send_data.php`;
const AI_DEBOUNCE_MS = 500;
const AI_MIN_QUERY_LEN = 3;

// A lightweight, token-based search over a caller-supplied list of
// { label, method, info, advanced } commands — every query word must
// appear as a substring somewhere in the label/info, in any order, but
// there's no typo tolerance or edit-distance scoring. See
// usability-recommendations.md rec #4.
//
// When the literal match comes up empty, falls back to an AI-assisted
// search: the query plus the (already mode-filtered) command list's
// label/info are sent to command_search.php, which asks a model to pick
// matching labels from that closed set — it can never return a command
// that wasn't in the list, so this can't surface something Basic mode
// hides or an action that doesn't exist. Purely a recall aid on top of
// the same literal list; nothing about execution changes.
//
// If even that finds nothing, an explicit "Ask AI" tap sends the question
// to docs_qa.php, which answers from the app's own documentation (not a
// closed set — free-form, can give step-by-step instructions). This is
// opt-in rather than automatic since it's a much bigger, doc-stuffed
// request than command routing.
// Static, hand-maintained for now — mirrors andiamo-web/public/Docs/
// HelpVideosAll.html's topic list, keyed by a slugified title (there's no
// stable ID on command objects to key off instead; see the
// project_commandsearch_video_idea memory for why a blanket ID retrofit
// across every menu item wasn't worth it just for this). Swap for a real
// endpoint later if this proves worth automating — commands whose
// `videoKey` isn't in this map just don't get a "Watch how" link, so
// missing entries fail silently rather than breaking anything.
const HELP_VIDEO_MAP_URL = "/Data/HelpVideoMap.json";

const CommandPalette = (props) => {
  const [query, setQuery] = useState("");
  const [videoMap, setVideoMap] = useState({});
  const [aiLoading, setAiLoading] = useState(false);
  const [aiMatches, setAiMatches] = useState([]);
  const [aiError, setAiError] = useState(false);
  const [askState, setAskState] = useState("idle"); // idle | loading | answered | error
  const [askAnswer, setAskAnswer] = useState("");
  const [micState, setMicState] = useState("idle"); // idle | recording | transcribing | error
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);
  const askAbortRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const micStreamRef = useRef(null);

  useEffect(() => {
    if (props.show) {
      setQuery("");
      setAiMatches([]);
      setAiError(false);
      setAskState("idle");
      setAskAnswer("");
      setMicState("idle");
      setTimeout(() => inputRef.current && inputRef.current.focus(), 60);
      // Best-effort — a missing/failed fetch just means no "Watch how"
      // links show up this session, not a broken palette.
      fetch(HELP_VIDEO_MAP_URL)
        .then((r) => r.json())
        .then(setVideoMap)
        .catch(() => {});
    } else {
      stopMicStream();
    }
    return () => {
      clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
      if (askAbortRef.current) askAbortRef.current.abort();
      stopMicStream();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.show]);

  // Abandons any in-progress recording without transcribing it — used when
  // the palette closes/unmounts mid-recording, as opposed to toggleMic's
  // user-initiated "stop and transcribe" path.
  const stopMicStream = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.onstop = null;
      mediaRecorderRef.current.stop();
    }
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((t) => t.stop());
      micStreamRef.current = null;
    }
  };

  const startRecording = async () => {
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      setMicState("error");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      micStreamRef.current = stream;
      audioChunksRef.current = [];
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        micStreamRef.current = null;
        const blob = new Blob(audioChunksRef.current, { type: recorder.mimeType || "audio/webm" });
        transcribe(blob);
      };
      recorder.start();
      setMicState("recording");
    } catch (e) {
      setMicState("error");
    }
  };

  const transcribe = (blob) => {
    setMicState("transcribing");
    const formData = new FormData();
    formData.append("audio_file", blob, "recording.webm");
    fetch(WHISPER_URL, { method: "POST", body: formData })
      .then((r) => r.json())
      .then((res) => {
        setMicState("idle");
        if (res.text) {
          setQuery(res.text.trim());
          setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
        } else {
          setMicState("error");
        }
      })
      .catch(() => setMicState("error"));
  };

  const toggleMic = () => {
    if (micState === "recording") {
      mediaRecorderRef.current && mediaRecorderRef.current.stop();
    } else if (micState === "idle" || micState === "error") {
      startRecording();
    }
  };

  const commands = props.commands || [];
  const q = query.trim().toLowerCase();
  const qTokens = q.split(/\s+/).filter(Boolean);

  // Token-based intersection rather than one whole-string substring check —
  // every word in the query has to appear somewhere in the label or info,
  // in any order, so phrasing like "schedule places" still matches "Build
  // Schedule from Places" the way a single .includes(q) never would. Still
  // simple per-token substring matching, no real fuzzy/typo tolerance —
  // just order-independent. Ranks label hits above info-only hits so a
  // command whose own name matches beats one that only mentions the words
  // in passing.
  // Hidden admin-unlock trigger — deliberately NOT a normal command entry
  // (which would partial-match every query containing "a", "ad", "adm"...
  // via the substring search below and be visible in the results list well
  // before the full word was typed). Only the exact word fires it.
  useEffect(() => {
    if (q === "admin" && props.onSecretMatch) props.onSecretMatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  let filtered = commands;
  if (q !== "") {
    filtered = commands
      .map((c) => {
        const label = c.label.toLowerCase();
        const info = (c.info || "").toLowerCase();
        let labelHits = 0;
        let hits = 0;
        for (const t of qTokens) {
          if (label.includes(t)) { labelHits++; hits++; }
          else if (info.includes(t)) hits++;
        }
        return { c, labelHits, hits };
      })
      .filter(({ hits }) => hits === qTokens.length)
      .sort((a, b) => {
        if (b.labelHits !== a.labelHits) return b.labelHits - a.labelHits;
        const aStarts = a.c.label.toLowerCase().startsWith(q) ? 0 : 1;
        const bStarts = b.c.label.toLowerCase().startsWith(q) ? 0 : 1;
        return aStarts - bStarts;
      })
      .map(({ c }) => c);
  }

  // Debounced AI fallback — only fires when literal search found nothing.
  // Editing the query always abandons any in-progress "Ask AI" answer for
  // the previous question.
  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();
    if (askAbortRef.current) askAbortRef.current.abort();
    setAiMatches([]);
    setAiError(false);
    setAskState("idle");
    setAskAnswer("");

    if (q.length < AI_MIN_QUERY_LEN || filtered.length > 0) {
      setAiLoading(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      const controller = new AbortController();
      abortRef.current = controller;
      setAiLoading(true);
      fetch(AI_SEARCH_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          q: query.trim(),
          commands: commands.map((c) => ({ label: c.label, info: c.info })),
        }),
        signal: controller.signal,
      })
        .then((r) => r.json())
        .then((res) => {
          setAiLoading(false);
          const labels = Array.isArray(res.matches) ? res.matches : [];
          const byLabel = new Map(commands.map((c) => [c.label, c]));
          const resolved = labels.map((l) => byLabel.get(l)).filter(Boolean);
          setAiMatches(resolved);
          if (labels.length === 0 || resolved.length === 0) setAiError(res.error ? true : false);
        })
        .catch((e) => {
          if (e.name === "AbortError") return;
          setAiLoading(false);
          setAiError(true);
        });
    }, AI_DEBOUNCE_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const runCommand = (cmd) => {
    props.onHide();
    if (cmd.method) setTimeout(cmd.method, 0);
  };

  const watchHow = (e, cmd) => {
    e.stopPropagation(); // don't also trigger runCommand on the row
    window.open(videoMap[cmd.videoKey], "_blank", "noopener,noreferrer");
  };

  const renderWatchHow = (cmd) => {
    if (!cmd.videoKey || !videoMap[cmd.videoKey]) return null;
    return (
      <button
        className="cp-watch-how"
        onClick={(e) => watchHow(e, cmd)}
        title="Watch how"
        aria-label={`Watch how: ${cmd.label}`}
      >
        <FontAwesomeIcon icon={solid("circle-play")} />
        Watch how
      </button>
    );
  };

  const askAI = () => {
    const question = query.trim();
    if (!question) return;
    if (askAbortRef.current) askAbortRef.current.abort();
    const controller = new AbortController();
    askAbortRef.current = controller;
    setAskState("loading");
    setAskAnswer("");
    fetch(DOCS_QA_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ q: question }),
      signal: controller.signal,
    })
      .then((r) => r.json())
      .then((res) => {
        if (res.answer) {
          setAskAnswer(res.answer);
          setAskState("answered");
        } else {
          setAskState("error");
        }
      })
      .catch((e) => {
        if (e.name === "AbortError") return;
        setAskState("error");
      });
  };

  const handleKeyDown = (e) => {
    if (e.key !== "Enter") return;
    if (filtered.length > 0) runCommand(filtered[0]);
    else if (aiMatches.length > 0) runCommand(aiMatches[0]);
  };

  const showAiSection = q.length >= AI_MIN_QUERY_LEN && filtered.length === 0;
  const showAskRow = showAiSection && !aiLoading && aiMatches.length === 0 && askState === "idle";

  return (
    <div className="modal">
      <Modal
        show={props.show}
        onHide={props.onHide}
        animation={false}
        dialogClassName="myModalClass mm-dialog cp-dialog"
        aria-labelledby="cp-title"
        centered
        scrollable={false}
      >
        <Modal.Body style={{ padding: 0 }}>
          <div className="mm-shell">
            <div className="mm-header">
              <div className="mm-header-icon">
                <FontAwesomeIcon icon={solid("magnifying-glass")} />
              </div>
              <input
                ref={inputRef}
                className="cp-input"
                type="text"
                placeholder="What do you want to do?"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                aria-label="Search commands"
                id="cp-title"
              />
              <button
                className={`cp-mic${micState === "recording" ? " cp-mic-recording" : ""}`}
                onClick={toggleMic}
                disabled={micState === "transcribing"}
                aria-label={micState === "recording" ? "Stop recording" : "Search by voice"}
                title={
                  micState === "recording"
                    ? "Stop recording"
                    : micState === "transcribing"
                    ? "Transcribing…"
                    : micState === "error"
                    ? "Voice search unavailable — try again"
                    : "Search by voice"
                }
              >
                <FontAwesomeIcon
                  icon={micState === "transcribing" ? solid("spinner") : solid("microphone")}
                  spin={micState === "transcribing"}
                />
              </button>
              <button className="mm-close" onClick={props.onHide} aria-label="Close">
                <FontAwesomeIcon icon={solid("xmark")} />
              </button>
            </div>
            {micState === "error" ? (
              <div className="cp-mic-error">Couldn't use the microphone — check permissions and try again.</div>
            ) : null}
            {askState !== "idle" ? (
              <div className="cp-answer">
                <div className="cp-ai-label">Answer</div>
                {askState === "loading" ? (
                  <div className="cp-empty">Thinking…</div>
                ) : askState === "error" ? (
                  <div className="cp-empty">Couldn't get an answer — try again.</div>
                ) : (
                  <div className="cp-answer-text">{askAnswer}</div>
                )}
              </div>
            ) : (
              <div className="mm-list">
                {filtered.length > 0 ? (
                  filtered.map((cmd, i) => (
                    <div
                      key={`${cmd.label}-${i}`}
                      className="mm-item"
                      style={{ "--i": i }}
                      onClick={() => runCommand(cmd)}
                    >
                      <span className="mm-item-label">{cmd.label}</span>
                      {renderWatchHow(cmd)}
                    </div>
                  ))
                ) : showAiSection ? (
                  aiLoading ? (
                    <div className="cp-empty">Searching…</div>
                  ) : aiMatches.length > 0 ? (
                    <>
                      <div className="cp-ai-label">Suggested</div>
                      {aiMatches.map((cmd, i) => (
                        <div
                          key={`ai-${cmd.label}-${i}`}
                          className="mm-item"
                          style={{ "--i": i }}
                          onClick={() => runCommand(cmd)}
                        >
                          <span className="mm-item-label">{cmd.label}</span>
                          {renderWatchHow(cmd)}
                        </div>
                      ))}
                    </>
                  ) : (
                    <>
                      <div className="cp-empty">
                        {aiError ? "Search is temporarily unavailable" : "No matching commands"}
                      </div>
                      {showAskRow ? (
                        <div className="mm-item cp-ask-row" onClick={askAI}>
                          <FontAwesomeIcon icon={solid("wand-magic-sparkles")} className="cp-ask-icon" />
                          <span className="mm-item-label">Ask AI: "{query.trim()}"</span>
                        </div>
                      ) : null}
                    </>
                  )
                ) : (
                  <div className="cp-empty">No matching commands</div>
                )}
              </div>
            )}
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default CommandPalette;
